import { CreateMainCategoryInput, UpdateMainCategoryInput } from "./../models/GQL_API";
import { ListingByConceptVariables, Option } from "../models/app";
import { GRAPHQL_AUTH_MODE } from "@aws-amplify/api";
import { API } from "aws-amplify";
import { GraphQLQuery } from "@aws-amplify/api";
import { useDispatch, useSelector } from "react-redux";
import useApp from "./useApp";
import { MainCategory } from "../models";
import { createMainCategory, updateMainCategory } from "../graphql/mutations";
import { getMainCategory, listMainCategories } from "../graphql/queries";
import { HeadCell } from "../models/dataTable";
import { setListing } from "../store/ducks/mainCategory";
import { getDateFormatted } from "../helpers/utils";

const useResource = (listingName: string, singleName: string) => {
  const dispatch = useDispatch();
  const { showError, showConfirm } = useApp();

  const session = useSelector((state: any) => state.app.session);
  const mainCategoriesListing = useSelector((state: any) => state.mainCategories.listing);

  const nextToken= useSelector((state: any) => state.mainCategories.nextToken);
  const lastIndex= useSelector((state: any) => state.mainCategories.lastIndex);
  const paginationListing = useSelector((state: any) => state.mainCategories.pagination);
  const selectedConcept = useSelector((state: any) => state.concepts.selected);

  async function fetch(params: ListingByConceptVariables) {
    try {
      const { conceptID, searchText ,limit,startIndex } = params;
      const filter: any = {
        conceptID: { eq: conceptID?conceptID:selectedConcept}, 
      };
      const listing: any = await API.graphql<GraphQLQuery<MainCategory>>({
        query: listMainCategories,
        variables: { filter, limit: limit ?? 1000},
        authMode: session
          ? GRAPHQL_AUTH_MODE.AMAZON_COGNITO_USER_POOLS
          : GRAPHQL_AUTH_MODE.AWS_IAM,
      });

      const listSorted=listing.data.listMainCategories.items.sort((a:any, b:any) => {
        return a.precedence - b.precedence;
    })

    if (searchText.length > 0)
      {
        let filteredData=listSorted.filter((item:any)=>{ return item.name.toLowerCase().includes(searchText.toLowerCase())})
        return filteredData;
      }

      return listSorted;
    
    } catch (err) {
      showError(err);
    }
  }

  async function get(params: any) {
    const { id } = params;

    try {
      const mainCategory: any = await API.graphql({
        query: getMainCategory,
        variables: { id },
        authMode: session
          ? GRAPHQL_AUTH_MODE.AMAZON_COGNITO_USER_POOLS
          : GRAPHQL_AUTH_MODE.AWS_IAM,
      });

      return mainCategory.data.getMainCategory;
    } catch (err) {
      throw err;
    }
  }

  async function create(params: any, session = true) {
    let { userID, userName, data } = params;

    try {
      const createInput: CreateMainCategoryInput = {
        name: data.name.toLowerCase(),        
        precedence: data.precedence ? data.precedence : "0",
        openTime: data.openTime ? data.openTime :null,
        closeTime: data.closeTime ? data.closeTime :null,
        conceptID: data.conceptID,
        createdAt: getDateFormatted(new Date()),
        createdByID: userID,
        createdByName: userName,
      };

      const mainCategory = await API.graphql<GraphQLQuery<MainCategory>>({
        query: createMainCategory,
        variables: { input: createInput },
        authMode: session
          ? GRAPHQL_AUTH_MODE.AMAZON_COGNITO_USER_POOLS
          : GRAPHQL_AUTH_MODE.AWS_IAM,
      });

      showConfirm(`New ${singleName} has been created successfully`);

      return mainCategory;
    } catch (err) {
      console.log(err);
    }
  }

  async function update(params: any, session = false) {
    try {
      const { data } = params;
      let original = await get(params);

      const updateInput: UpdateMainCategoryInput = {
        id: original.id,
        name: data.name ? data.name.toLowerCase() : original.name,
        precedence: data.precedence ? data.precedence : original.precedence,
        openTime: data.openTime ? data.openTime :original.openTime,
        closeTime: data.closeTime ? data.closeTime :original.closeTime,
        _version: original._version,
      };

      const mainCategory: any = await API.graphql<GraphQLQuery<MainCategory>>({
        query: updateMainCategory,
        variables: { input: updateInput },
        authMode: GRAPHQL_AUTH_MODE.AMAZON_COGNITO_USER_POOLS,
      });

      showConfirm(`${singleName} has been updated successfully`);

      return mainCategory.data.updateMainCategory;
    } catch (err) {
      showError(err);
    }
  }

  async function exportAll(params: ListingByConceptVariables) {
    const data = await fetch(params);
    return data;
  }

  function options(listing: MainCategory[]) {
    const options: Option[] = [];

    for (let option of listing) {
      options.push({ label: option.name, value: option.id });
    }

    return options;
  }

  const headCells: readonly HeadCell[] = [
    {
      id: "name",
      numeric: false,
      disablePadding: false,
      label: "Name",
    },
    {
      id: "precedence",
      numeric: false,
      disablePadding: false,
      label: "Precedence",
    },
    {
      id: "createdBy",
      numeric: false,
      disablePadding: false,
      label: "Created By",
    },
    {
      id: "createdAt",
      numeric: false,
      disablePadding: false,
      label: "Date",
    },
  ];
  const dataCells: readonly string[] = ["name", "precedence"];

  const api: any = {};

  api[`${listingName}Model`] = MainCategory as any;
  api[`${listingName}HeadCells`] = headCells;
  api[`${listingName}DataCells`] = dataCells;
  api[`${listingName}Fetch`] = fetch;
  api[`${listingName}Options`] = options;
  api[`${listingName}Get`] = get;
  api[`${listingName}Create`] = create;
  api[`${listingName}Update`] = update;
  api[`${listingName}Export`] = exportAll;
  api[`${listingName}ChangeListing`] = (listing: MainCategory[]) =>
    dispatch(setListing(listing));

  return api;
};

export default useResource;
